import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from 'react-dom';
import { Eye, Pencil, Trash2, ChevronDown, Upload } from "lucide-react";
import { Modal, Row, Col, Spinner } from "react-bootstrap";
import { fetchSops, createSop, updateSop, deleteSop, fetchUsers, type AdminUser, fetchUserProfile } from "../../services/api";
import { generateInitials } from "../../utils/userUtils";
import type { SopItem, SopUserRef } from "../../types";
import { toast } from "react-toastify";
import Preloader from "../../components/Preloder";
import { useUpload } from "../../contexts/UploadContext";
import { useTimezoneFormatting } from "../../hooks/useTimezoneFormatting";
import { logger } from "../../utils/logger";
import pdfIcon from "../../assets/icons/pdfIcon.png";
import sopUploadSuccessImage from "../../assets/icons/sop-upload-success-image.gif";

// Removed mock data; now fetching dynamically from backend

function SopUploadSuccessModal({
    show,
    onClose,
    sopTitle
}: {
    show: boolean;
    onClose: () => void;
    sopTitle: string;
}) {
    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header className="border-0 pb-0">
                <i className="iconoir-xmark text-20-reg cursor-pointer ml-auto" onClick={onClose}></i>
            </Modal.Header>
            <Modal.Body className="text-center pt-2 pb-3">
                <img
                    src={sopUploadSuccessImage}
                    alt="SOP Upload Success"
                    className="mx-auto mb-1"
                    style={{ width: '115px', height: '115px' }}
                />
                <h3 className="font-semibold text-red-600 mb-2">SOP uploaded successfully.</h3>
                <p className="text-16-reg text-grey-800 mb-3" title={sopTitle}>{truncateText(sopTitle, 20)} <br />
                    is now available to assigned users.</p>
            </Modal.Body>
        </Modal>
    );
}

function DeleteSopModal({
    sop,
    onClose,
    onConfirm
}: {
    sop: SopItem;
    onClose: () => void;
    onConfirm: () => void;
}) {
    return (
        <Modal show={true} onHide={onClose} className='mini-modal-width' centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Delete SOP?</Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <div className="text-left">
                    <p className="text-gray-600 mb-6">Are you sure you want to delete this SOP?</p>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-3">
                        <button
                            onClick={onClose}
                            className="renegade-btn-secondary mini-btn"
                        >
                            No, Keep
                        </button>
                        <button
                            onClick={onConfirm}
                            className="renegade-btn-primary mini-btn"
                        >
                            Yes, Delete
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}


// Helper function to determine role based on users (similar to EditSopModal logic)
const determineRoleFromUsers = (sopUsers: SopUserRef[], activeUsers: AdminUser[], currentRoles: SopItem["roles"]): SopItem["roles"] => {
    if (!sopUsers.length) return currentRoles; // Keep current roles if no users

    // Get roles of remaining active users
    const userRoles = sopUsers.map(sopUser => {
        if (!sopUser) return null;

        // Find the user by email or name
        const user = activeUsers.find(u =>
            u.email === sopUser.email ||
            u.name === sopUser.name ||
            u.email === sopUser.name // In case name is actually an email
        );
        return user?.role || null;
    }).filter(role => role !== null && role !== 'admin'); // Exclude admin users

    if (userRoles.length === 0) return currentRoles; // Keep current roles if no valid users found

    // Check if all remaining users are managers
    const allManagers = userRoles.every(role => role === 'manager');
    // Check if all remaining users are workers
    const allWorkers = userRoles.every(role => role === 'worker');

    if (allManagers) return ['manager'];
    if (allWorkers) return ['worker'];

    // If mixed roles or no clear pattern, return 'all'
    return ['all'];
};

// Helper function to filter out deleted/inactive users and recalculate roles
const filterActiveUsers = (sopUsers: SopUserRef[], activeUsers: AdminUser[], currentRoles: SopItem["roles"]): { users: SopUserRef[], roles: SopItem["roles"] } => {
    if (!activeUsers.length) return { users: sopUsers, roles: currentRoles }; // If no active users loaded yet, return original

    const activeEmails = new Set(activeUsers.map(user => user.email));
    const activeNames = new Set(activeUsers.map(user => user.name));

    const filteredUsers = sopUsers.filter(sopUser => {
        if (!sopUser) return false;

        // Check by email first
        if (sopUser.email && activeEmails.has(sopUser.email)) return true;

        // Check by name 
        if (sopUser.name && activeNames.has(sopUser.name)) return true;

        // If email contains @ and is not in active users, filter out
        if (sopUser.email && sopUser.email.includes('@') && !activeEmails.has(sopUser.email)) {
            return false;
        }

        return true; // Keep if we can't determine
    });

    // Recalculate roles based on remaining users
    const newRoles = determineRoleFromUsers(filteredUsers, activeUsers, currentRoles);

    return { users: filteredUsers, roles: newRoles };
};

const truncateText = (text: string, limit = 20): string => {
    if (!text) return "";
    if (text.length <= limit) return text;
    if (limit <= 2) return text.slice(0, limit);
    return `${text.slice(0, limit - 2)}...`;
};

export const AdminManageSop: React.FC = () => {
    const { formatToLocal } = useTimezoneFormatting();
    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(9); // match design rows per page
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [total, setTotal] = useState(0);
    const [items, setItems] = useState<SopItem[]>([]);
    const [openMenuForId, setOpenMenuForId] = useState<string | null>(null);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedSop, setSelectedSop] = useState<SopItem | null>(null);
    const [addNewSopModalOpen, setAddNewSopModalOpen] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successSopTitle, setSuccessSopTitle] = useState("");
    const hasShownSuccessModal = useRef(false);



    // Global upload context
    const { getUploads, closeSuccessModal, connectSSE, closeIndicator, showSuccessModal: globalShowSuccessModal, successSopTitle: globalSuccessSopTitle } = useUpload();
    const uploads = getUploads();

    // Debounce search to reduce API calls
    const debouncedSearch = useDebouncedValue(searchTerm, 350);

    // Watch for upload completion from global context
    useEffect(() => {
        if (globalShowSuccessModal && globalSuccessSopTitle && !hasShownSuccessModal.current) {
            setSuccessSopTitle(globalSuccessSopTitle);
            setShowSuccessModal(true);
            hasShownSuccessModal.current = true;
            // Close the global success modal state so it doesn't show again on other pages
            closeSuccessModal();
            // Refresh SOPs list once after upload completes
            refreshSopList();
        }
    }, [globalShowSuccessModal, globalSuccessSopTitle, closeSuccessModal]);

    // Removed auto-refresh to improve UX - users can manually refresh if needed
    // Upload progress is driven by UploadContext polling /sops/{file_id}/processing-status

    useEffect(() => {
        let isCancelled = false;
        async function load() {
            setLoading(true);
            setError(null);
            try {
                // Fetch SOPs and active users in parallel
                const [sopsRes, usersRes] = await Promise.all([
                    fetchSops({ page, pageSize, search: debouncedSearch }),
                    fetchUsers({ page: 1, pageSize: 1000, status: 'active' })
                ]);

                if (isCancelled) return;

                // Debug: Log the API response

                const normalized = normalizeSopListResponse(sopsRes);
                const activeUsers = usersRes.items || [];

                // Ensure users array is consistently shaped and filter out deleted users
                const safeItems = (normalized.items || []).map((it: any) => {
                    const rawUsers = Array.isArray(it.users) ? it.users : [];
                    const users = rawUsers
                        .map((u: any) => {
                            if (!u) return null;
                            if (typeof u === 'string') return { name: u, email: u };
                            const name = u.name || u.email || '';
                            const email = u.email || (name && name.includes('@') ? name : '');
                            return name || email ? { name, email } : null;
                        })
                        .filter(Boolean) as SopUserRef[];

                    // Filter out deleted/inactive users and recalculate roles
                    const roles = Array.isArray(it.roles) ? it.roles : (it.roles ? [it.roles] : []);
                    const { users: filteredUsers, roles: newRoles } = filterActiveUsers(users, activeUsers, roles);

                    return {
                        ...it,
                        users: filteredUsers,
                        roles: newRoles,
                        processing_status: it.processing_status || 'completed' // Default old SOPs to completed
                    };
                });
                setItems(safeItems);
                setTotal(normalized.total || 0);

                // Only set error if there's an actual error, not just an empty result
                if (sopsRes?.error || sopsRes?.status === 'error') {
                    setError(sopsRes.message || sopsRes.error || "Failed to load SOPs");
                } else {
                    setError(null); // Clear any previous errors
                }
            } catch (e: any) {
                if (isCancelled) return;
                setItems([]);
                setTotal(0);
                setError(e?.message || "Failed to load SOPs");
            } finally {
                if (!isCancelled) {
                    setLoading(false);
                    setInitialLoading(false); // Set initial loading to false after first load
                }
            }
        }
        load();
        return () => { isCancelled = true; };
    }, [page, pageSize, debouncedSearch, reloadKey]);

    const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

    const handleViewSop = (sop: SopItem) => {
        setSelectedSop(sop);
        setViewModalOpen(true);
    };

    const handleEditSop = (sop: SopItem) => {
        setSelectedSop(sop);
        setEditModalOpen(true);
    };

    const closeViewModal = () => {
        setViewModalOpen(false);
        setSelectedSop(null);
    };

    const closeEditModal = () => {
        setEditModalOpen(false);
        setSelectedSop(null);
    };

    const handleDeleteSop = (sop: SopItem) => {
        setSelectedSop(sop);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        setDeleteModalOpen(false);
        setSelectedSop(null);
    };

    const openAddNewSopModal = () => {
        setAddNewSopModalOpen(true);
    };

    const closeAddNewSopModal = () => {
        setAddNewSopModalOpen(false);
    };

    // Function to refresh the SOP list
    const refreshSopList = () => {
        // Add a small delay to ensure backend has processed the upload
        setTimeout(() => {
            // Reset to first page and trigger a reload
            setPage(1);
            // The useEffect will automatically reload due to page change
            setReloadKey((k) => k + 1);
        }, 500);
    };

    // Show preloader while data is loading
    if (initialLoading) {
        return <Preloader />;
    }

    return (
        <div className="space-y-6 min-h-0">


            {/* Controls */}
            <div className="px-0">
                <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-stretch md:items-center md:justify-between pt-2 md:pt-3">
                    <div className="w-full md:w-80 shrink-0 pl-2 md:pl-3">
                        <div className="relative">
                            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                                <img src="/icons/searchIcon.png" alt="Search" className="w-4 h-4" />
                            </span>
                            <input
                                type="text"
                                placeholder="Search SOPs..."
                                value={searchTerm}
                                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                                className="w-full h-10 pl-9 pr-4 py-1 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm"
                            />
                        </div>
                    </div>
                    <div className="flex-none pr-2 md:pr-3">
                        <button
                            onClick={openAddNewSopModal}
                            className="renegade-btn-primary mini-btn"
                            title="Upload SOP"
                        >
                            <svg width="26" height="26" viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M0.5 13C0.5 6.09625 6.09625 0.5 13 0.5C19.9037 0.5 25.5 6.09625 25.5 13C25.5 19.9037 19.9037 25.5 13 25.5C6.09625 25.5 0.5 19.9037 0.5 13ZM13 3C10.3478 3 7.8043 4.05357 5.92893 5.92893C4.05357 7.8043 3 10.3478 3 13C3 15.6522 4.05357 18.1957 5.92893 20.0711C7.8043 21.9464 10.3478 23 13 23C15.6522 23 18.1957 21.9464 20.0711 20.0711C21.9464 18.1957 23 15.6522 23 13C23 10.3478 21.9464 7.8043 20.0711 5.92893C18.1957 4.05357 15.6522 3 13 3Z" fill="white" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M14.25 6.75C14.25 6.41848 14.1183 6.10054 13.8839 5.86612C13.6495 5.6317 13.3315 5.5 13 5.5C12.6685 5.5 12.3505 5.6317 12.1161 5.86612C11.8817 6.10054 11.75 6.41848 11.75 6.75V11.75H6.75C6.41848 11.75 6.10054 11.8817 5.86612 12.1161C5.6317 12.3505 5.5 12.6685 5.5 13C5.5 13.3315 5.6317 13.6495 5.86612 13.8839C6.10054 14.1183 6.41848 14.25 6.75 14.25H11.75V19.25C11.75 19.5815 11.8817 19.8995 12.1161 20.1339C12.3505 20.3683 12.6685 20.5 13 20.5C13.3315 20.5 13.6495 20.3683 13.8839 20.1339C14.1183 19.8995 14.25 19.5815 14.25 19.25V14.25H19.25C19.5815 14.25 19.8995 14.1183 20.1339 13.8839C20.3683 13.6495 20.5 13.3315 20.5 13C20.5 12.6685 20.3683 12.3505 20.1339 12.1161C19.8995 11.8817 19.5815 11.75 19.25 11.75H14.25V6.75Z" fill="white" />
                            </svg>
                            Upload SOP
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">SOP Title</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Uploaded By</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Uploaded On</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Roles</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase">Users</th>
                                <th className="px-4 py-4 text-12-bold grey-700 uppercase"></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-4 py-4 text-14-bold grey-800">Loading...</td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-6 text-center text-red-600">{error}</td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center !text-gray-500">No SOPs found</td>
                                </tr>
                            ) : (
                                items.map((sop) => (
                                    <tr key={sop.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-14-bold grey-800" title={sop.title}>{truncateText(sop.title, 20)}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{sop.uploadedBy}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{formatDate(sop.uploadedOn)}</td>
                                        <td className="px-4 py-4 text-14-reg grey-800">{renderRoles(sop.roles)}</td>
                                        <td className="px-6 py-4 text-14-reg grey-800">{renderUsers(sop.users)}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {sop.processing_status === 'completed' || sop.processing_status === 'failed' ? (
                                                <RowActions
                                                    sop={sop}
                                                    isOpen={openMenuForId === sop.id}
                                                    onToggle={() => setOpenMenuForId((v) => v === sop.id ? null : sop.id)}
                                                    onClose={() => setOpenMenuForId(null)}
                                                    onView={() => handleViewSop(sop)}
                                                    onEdit={() => handleEditSop(sop)}
                                                    onDelete={() => handleDeleteSop(sop)}
                                                />
                                            ) : (
                                                <span className="text-sm text-gray-400">Processing...</span>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="global-pagination px-4 md:px-6 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            className="btn-next-prev"
                            disabled={page === 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                            <i className="iconoir-nav-arrow-left"></i>
                        </button>
                        <div className="flex items-center gap-1">
                            {getPageNumbers(page, totalPages).map((p: PageButton) => (
                                <button
                                    key={p.key}
                                    disabled={p.disabled}
                                    onClick={() => typeof p.page === 'number' && setPage(p.page)}
                                    className={`btn-page-number ${p.active ? 'color-renegade' : ''}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                        <button
                            className="btn-next-prev"
                            disabled={page >= totalPages}
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        >
                            <i className="iconoir-nav-arrow-right"></i>
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min="1"
                            max="100"
                            value={pageSize}
                            onChange={(e) => {
                                const newPageSize = parseInt(e.target.value) || 10;
                                if (newPageSize >= 1 && newPageSize <= 100) {
                                    setPageSize(newPageSize);
                                    setPage(1); // Reset to first page when page size changes
                                }
                            }}
                            className="page-size-input"
                        />
                        <span className="text-14-bold grey-400">Rows per page</span>
                    </div>
                </div>
            </div>

            {/* View SOP Modal */}
            {viewModalOpen && selectedSop && (
                <ViewSopModal
                    sop={selectedSop}
                    onClose={closeViewModal}
                    onEdit={() => {
                        setViewModalOpen(false);
                        setEditModalOpen(true);
                    }}
                />
            )}

            {/* Edit SOP Modal */}
            {editModalOpen && selectedSop && (
                <EditSopModal
                    sop={selectedSop}
                    onClose={closeEditModal}
                    onUpdate={async (updatedSop: Partial<SopItem>) => {
                        try {
                            const roleRaw = Array.isArray(updatedSop.roles)
                                ? String(updatedSop.roles[0] || 'worker')
                                : String(updatedSop.roles || 'worker');
                            const roleMap: Record<string, string> = { worker: 'worker', field_worker: 'worker', manager: 'manager', all: 'all' };
                            const user_role = roleMap[roleRaw] || 'worker';

                            const specific_users = (updatedSop.users || [])
                                .map((u: any) => (u?.email && u.email.includes('@')) ? u.email : (u?.name || ''))
                                .filter((v: string) => !!v);

                            const apiPayload = {
                                title: updatedSop.title || selectedSop.title,
                                description: updatedSop.description !== undefined ? updatedSop.description : (selectedSop.description || ''),
                                user_role,
                                specific_users,
                            };
                            await updateSop(selectedSop.id, apiPayload);

                            // Optimistically update local list so UI reflects immediately
                            setItems((prev) => prev.map((it) => {
                                if (it.id !== selectedSop.id) return it;
                                const newUsers = specific_users.map((email: string) => ({ name: email, email }));
                                const newRoles = [user_role];
                                return {
                                    ...it,
                                    title: apiPayload.title,
                                    description: apiPayload.description,
                                    roles: newRoles,
                                    users: newUsers,
                                } as SopItem;
                            }));
                            closeEditModal();
                            // Also refresh in background to stay in sync with server
                            refreshSopList();
                        } catch (e: any) {
                            logger.error('ManageSop: Update failed:', e);
                            alert(e?.message || 'Failed to update SOP');
                        }
                    }}
                />
            )}

            {/* Delete SOP Modal */}
            {deleteModalOpen && selectedSop && (
                <DeleteSopModal
                    sop={selectedSop}
                    onClose={closeDeleteModal}
                    onConfirm={async () => {
                        try {
                            await deleteSop(selectedSop.id);
                            closeDeleteModal();
                            refreshSopList();
                        } catch (e: any) {
                            alert(e?.message || 'Failed to delete SOP');
                        }
                    }}
                />
            )}

            {/* Add New SOP Modal */}
            {addNewSopModalOpen && (
                <AddNewSopModal
                    onClose={closeAddNewSopModal}
                    onSuccess={(sopTitle) => {
                        refreshSopList();
                    }}
                />
            )}

            {/* SOP Upload Success Modal */}
            <SopUploadSuccessModal
                show={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    hasShownSuccessModal.current = false; // Reset for future uploads
                }}
                sopTitle={successSopTitle}
            />

        </div>
    );
};

function formatDate(value?: string) {
    if (!value) return "-";
    // Ensure timestamp is treated as UTC
    const normalizedTimestamp = value.endsWith('Z') ? value : value + 'Z';
    const date = new Date(normalizedTimestamp);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
}


function renderRoles(roles: SopItem["roles"]) {
    const values = Array.isArray(roles) ? roles : roles ? [roles] : [];
    if (values.length === 0) return <span className="text-sm text-gray-500">-</span>;
    return (
        <div className="flex flex-wrap gap-2">
            {values.map((r) => (
                <span key={r} className="text-14-reg grey-800">{toTitleCase(r)}</span>
            ))}
        </div>
    );
}

function renderRolesForView(roles: SopItem["roles"]) {
    const values = Array.isArray(roles) ? roles : roles ? [roles] : [];

    if (values.length === 0) return null;

    // Check if both manager and worker are present
    const hasManager = values.includes('manager') || values.includes('all');
    const hasWorker = values.includes('worker') || values.includes('all');

    const roleBoxes = [];

    if (hasManager) {
        roleBoxes.push(
            <div key="manager" className="px-3 py-1 bg-gray-200 rounded-md">
                <span className="text-14-reg grey-800">Manager</span>
            </div>
        );
    }

    if (hasWorker) {
        roleBoxes.push(
            <div key="worker" className="px-3 py-1 bg-gray-200 rounded-md">
                <span className="text-14-reg grey-800">Field Worker</span>
            </div>
        );
    }

    return roleBoxes;
}

function renderUsers(users: SopItem["users"]) {
    if (!users || users.length === 0) return <span className="text-sm text-gray-500">-</span>;
    const maxVisible = 3;
    const visible = users.slice(0, maxVisible);
    const extra = users.length - visible.length;

    const allNames = users.map((u) => u.name || u.email || "Unknown").join(", ");

    return (
        <div className="flex items-center ml-1">
            {visible.map((u, idx) => (
                <UserBadge
                    key={`${u.email || u.name || idx}`}
                    label={generateInitials(u.name || u.email || "?")}
                    tooltip={u.name || u.email || "Unknown"}
                />
            ))}
            {extra > 0 && (
                <UserBadge
                    label={`+${extra}`}
                    tooltip={allNames}
                    wide
                />
            )}
        </div>
    );
}


function toTitleCase(text: string) {
    return text.replace(/_/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

function normalizeSopListResponse(res: any): { items: SopItem[]; total: number } {
    // Try to normalize various possible shapes
    const items: SopItem[] =
        res?.items ||
        res?.results ||
        res?.data?.items ||
        res?.data?.results ||
        res?.data ||
        [];
    const total: number =
        res?.total || res?.count || res?.data?.total || res?.data?.count || items.length || 0;
    return { items, total };
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), delayMs);
        return () => clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
}

function UserBadge({ label, tooltip, wide = false }: { label: string; tooltip: string; wide?: boolean }) {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const badgeRef = useRef<HTMLSpanElement>(null);

    const handleMouseEnter = (e: React.MouseEvent) => {
        if (badgeRef.current) {
            const rect = badgeRef.current.getBoundingClientRect();
            setTooltipPosition({
                top: rect.bottom + 8, // 8px gap below badge
                left: rect.left + (rect.width / 2) // Center horizontally
            });
        }
        setShowTooltip(true);
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    return (
        <>
            <span
                ref={badgeRef}
                className="sop-user-badge"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {label}
            </span>
            {showTooltip && (
                <div
                    className={`custom-tooltip ${wide ? 'wide-tooltip' : ''}`}
                    style={{
                        position: 'fixed',
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: 'translateX(-50%)',
                        zIndex: 9999
                    }}
                >
                    {tooltip}
                    <div className="custom-tooltip-arrow" />
                </div>
            )}
        </>
    );
}

function RowActions({
    sop,
    isOpen,
    onToggle,
    onClose,
    onView,
    onEdit,
    onDelete,
}: {
    sop: SopItem;
    isOpen: boolean;
    onToggle: () => void;
    onClose: () => void;
    onView: () => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

    function computePosition() {
        const btn = buttonRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();

        // Use measured size if available, otherwise sensible defaults
        const menuWidth = (menuRef.current?.offsetWidth) ?? 224;
        const menuHeight = (menuRef.current?.offsetHeight) ?? 200;

        const margin = 8;
        // Prefer bottom-right aligned to trigger right edge
        let top = rect.bottom + margin;
        let left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - margin);

        // Flip up if not enough room below
        if (top + menuHeight > window.innerHeight) {
            top = rect.top - menuHeight - margin;
        }
        // Clamp within viewport
        if (top < margin) top = margin;
        if (left < margin) left = margin;

        setCoords({ top, left });
    }

    useLayoutEffect(() => {
        if (!isOpen) return;
        computePosition();
        const handle = () => computePosition();
        window.addEventListener('scroll', handle, true);
        window.addEventListener('resize', handle);
        return () => {
            window.removeEventListener('scroll', handle, true);
            window.removeEventListener('resize', handle);
        };
    }, [isOpen]);

    useEffect(() => {
        function onDocClick(e: MouseEvent) {
            const target = e.target as Node;
            if (
                menuRef.current && !menuRef.current.contains(target) &&
                buttonRef.current && !buttonRef.current.contains(target)
            ) onClose();
        }
        function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
        if (isOpen) {
            document.addEventListener('mousedown', onDocClick);
            document.addEventListener('keydown', onKey);
        }
        return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
    }, [isOpen, onClose]);

    // Render the menu with visibility hidden first to allow measurement, then show
    const shouldRender = isOpen;
    const isVisible = !!coords;
    const menu = shouldRender ? (
        <div
            ref={menuRef}
            style={{ position: 'fixed', top: (coords?.top ?? -9999), left: (coords?.left ?? -9999), zIndex: 1000, visibility: isVisible ? 'visible' : 'hidden' }}
            className="w-56 rounded-2xl bg-white shadow-lg ring-1 ring-black/5 border border-gray-200"
        >
            <div className="py-2">
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onView();
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                        <Eye size={16} />
                    </span>
                    <span className="text-sm text-gray-800">View</span>
                </button>
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onEdit();
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-green-50 text-green-600 flex items-center justify-center">
                        <Pencil size={14} />
                    </span>
                    <span className="text-sm text-gray-800">Edit</span>
                </button>
                <button
                    className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50"
                    onClick={() => {
                        onDelete();
                        onClose();
                    }}
                >
                    <span className="w-6 h-6 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                        <Trash2 size={16} />
                    </span>
                    <span className="text-sm text-gray-800">Delete</span>
                </button>
            </div>
        </div>
    ) : null;

    return (
        <div ref={containerRef} className="relative inline-block text-left">
            <button ref={buttonRef} type="button" onClick={() => { onToggle(); setTimeout(computePosition, 0); }} className="px-2 py-1 rounded-lg hover:bg-gray-100">
                <svg width="26" height="6" viewBox="0 0 26 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6C2.20435 6 1.44129 5.68393 0.87868 5.12132C0.316071 4.55871 0 3.79565 0 3C0 2.20435 0.316071 1.44129 0.87868 0.878679C1.44129 0.31607 2.20435 0 3 0C3.79565 0 4.55871 0.31607 5.12132 0.878679C5.68393 1.44129 6 2.20435 6 3C6 3.79565 5.68393 4.55871 5.12132 5.12132C4.55871 5.68393 3.79565 6 3 6ZM13 6C12.2044 6 11.4413 5.68393 10.8787 5.12132C10.3161 4.55871 10 3.79565 10 3C10 2.20435 10.3161 1.44129 10.8787 0.878679C11.4413 0.31607 12.2044 0 13 0C13.7956 0 14.5587 0.31607 15.1213 0.878679C15.6839 1.44129 16 2.20435 16 3C16 3.79565 15.6839 4.55871 15.1213 5.12132C14.5587 5.68393 13.7956 6 13 6ZM23 6C22.2044 6 21.4413 5.68393 20.8787 5.12132C20.3161 4.55871 20 3.79565 20 3C20 2.20435 20.3161 1.44129 20.8787 0.878679C21.4413 0.31607 22.2044 0 23 0C23.7956 0 24.5587 0.31607 25.1213 0.878679C25.6839 1.44129 26 2.20435 26 3C26 3.79565 25.6839 4.55871 25.1213 5.12132C24.5587 5.68393 23.7956 6 23 6Z" fill="#616161" />
                </svg>
            </button>
            {menu && createPortal(menu, document.body)}
        </div>
    );
}

type PageButton = { key: string; page?: number; label: string | number; active?: boolean; disabled?: boolean };

function getPageNumbers(current: number, total: number): PageButton[] {
    const buttons: PageButton[] = [];
    const pushPage = (p: number, active = false) => buttons.push({ key: `p-${p}`, page: p, label: p, active });
    const pushEllipsis = (id: string) => buttons.push({ key: `e-${id}`, label: '…', disabled: true });

    if (total <= 5) {
        for (let i = 1; i <= total; i++) pushPage(i, i === current);
        return buttons;
    }

    // Always include first
    pushPage(1, current === 1);

    // Left ellipsis
    if (current > 3) pushEllipsis('l');

    // Middle pages around current
    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let i = start; i <= end; i++) pushPage(i, i === current);

    // Right ellipsis
    if (current < total - 2) pushEllipsis('r');

    // Last page
    pushPage(total, current === total);

    return buttons;
}

function ViewSopModal({ sop, onClose, onEdit }: { sop: SopItem; onClose: () => void; onEdit: () => void }) {
    // Get all unique users from the SOP (already filtered)
    const allUsers = sop.users || [];
    const userNames = allUsers.length > 0
        ? allUsers.map(u => u.name || u.email || 'Unknown').join(', ')
        : 'No active users assigned';

    return (
        <Modal show={true} onHide={onClose} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title>
                    {sop.title}
                    <button
                        onClick={onEdit}
                        className="p-2 rounded-lg hover:bg-gray-100"
                    >
                        <Pencil size={16} className="text-blue-600" />
                    </button>
                </Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-2 py-4">
                {/* Description */}
                <div className="px-6 pb-4">
                    <p className="text-16-reg grey-600">
                        {sop.description || 'No description available for this SOP.'}
                    </p>
                </div>

                {/* User Role Section */}
                <div className="px-6 pb-4">
                    <h3 className="text-14-bold grey-800 mb-2">User Role</h3>
                    {allUsers.length === 0 ? (
                        <p className="text-16-reg grey-600">—</p>
                    ) : (
                        <div className="flex gap-2">
                            {renderRolesForView(sop.roles)}
                        </div>
                    )}
                </div>

                {/* Specific Users Section */}
                <div className="px-6 pb-4">
                    <h3 className="text-14-bold grey-800 mb-2">Specific Users</h3>
                    <p className="text-16-reg grey-600">
                        {userNames}
                    </p>
                </div>

                {/* Attached Document Section */}
                <div className="px-6 pb-6">
                    <div className="flex items-center gap-3">
                        <img src={pdfIcon} alt="PDF" className="w-8 h-8" />
                        <span className="text-gray-800 font-medium">
                            {sop.file_name
                                ? sop.file_name
                                : `${sop.id}: ${sop.title}`
                            }
                        </span>
                    </div>
                </div>

            </Modal.Body>
        </Modal>
    );
}

function EditSopModal({
    sop,
    onClose,
    onUpdate
}: {
    sop: SopItem;
    onClose: () => void;
    onUpdate: (updatedSop: Partial<SopItem>) => void;
}) {
    // Determine the initial user role from SOP data
    const getInitialUserRole = () => {
        if (Array.isArray(sop.roles)) {
            const role = sop.roles[0] || 'worker';
            // If the role is 'all', we need to determine what to show in the dropdown
            // For editing purposes, 'all' should be mapped to 'all' to maintain the current behavior
            return role;
        }
        return sop.roles || 'worker';
    };

    // Form data interface
    interface FormData {
        title: string;
        description: string;
        userRole: 'manager' | 'worker' | 'all' | '';
        specificUsers: string[];
    }

    // Normalize user identifiers to always use emails and filter out deleted users
    const normalizeUserIdentifiers = (sopUsers: SopUserRef[], allUsers: AdminUser[]): string[] => {
        return sopUsers.map(sopUser => {
            if (!sopUser) return null;

            // If we already have an email, check if user still exists
            if (sopUser.email) {
                const existsInActiveUsers = allUsers.some(u => u.email === sopUser.email);
                return existsInActiveUsers ? sopUser.email : null;
            }

            // If we have an ID, try to find the user by ID first
            if (sopUser.id) {
                const foundUser = allUsers.find(u => u.id === sopUser.id);
                return foundUser ? foundUser.email : null;
            }

            // If we have a name, try to find the corresponding email
            if (sopUser.name) {
                const foundUser = allUsers.find(u =>
                    u.name === sopUser.name ||
                    u.email === sopUser.name // In case the name is actually an email
                );
                return foundUser ? foundUser.email : null;
            }

            return null;
        }).filter((email): email is string => email !== null); // Type-safe filter
    };


    // Handle user selection change with auto-detection
    const handleUserSelectionChange = (newSelectedUsers: string[]) => {

        setFormData(prev => {
            const newFormData = { ...prev, specificUsers: newSelectedUsers };

            // Auto-detect role if current role is 'all' and we have users selected
            if (prev.userRole === 'all' && newSelectedUsers.length > 0) {
                const detectedRole = determineUserRoleFromUsers(newSelectedUsers);
                if (detectedRole && detectedRole !== 'all') {
                    newFormData.userRole = detectedRole;
                }
            }
            return newFormData;
        });
    };

    const [formData, setFormData] = useState<FormData>({
        title: sop.title || '',
        description: sop.description || 'Enter SOP description',
        userRole: getInitialUserRole() as 'manager' | 'worker' | 'all',
        specificUsers: [] as string[] // Will be populated after users are loaded
    });

    // Dynamic users for Specific Users dropdown (like Add New SOP)
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isUserRoleDropdownOpen, setIsUserRoleDropdownOpen] = useState(false);
    const [isSpecificUserDropdownOpen, setIsSpecificUserDropdownOpen] = useState(false);
    const userRoleDropdownRef = useRef<HTMLDivElement>(null);
    const specificUserDropdownRef = useRef<HTMLDivElement>(null);

    // Validation errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Debug: Log initial form data

    // Update form data when sop prop changes (except specificUsers which are handled in users loading effect)
    useEffect(() => {
        const initialUserRole = Array.isArray(sop.roles) ? sop.roles[0] || 'worker' : sop.roles || 'worker';
        setFormData(prev => ({
            ...prev,
            title: sop.title || '',
            description: sop.description || 'Enter SOP description',
            userRole: initialUserRole as 'manager' | 'worker' | 'all'
        }));
    }, [sop]);

    // Fetch users on mount and normalize specific users
    useEffect(() => {
        const loadUsers = async () => {
            try {
                setUsersLoading(true);
                const res = await fetchUsers({ page: 1, pageSize: 100, status: 'active' });
                setUsers(res.items);

                // Normalize specific users after users are loaded
                if (sop.users && sop.users.length > 0) {
                    const normalizedUsers = normalizeUserIdentifiers(sop.users, res.items);
                    setFormData(prev => ({
                        ...prev,
                        specificUsers: normalizedUsers
                    }));
                }
            } catch (error) {
                logger.error('Error fetching users:', error);
            } finally {
                setUsersLoading(false);
            }
        };
        loadUsers();
    }, [sop]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (userRoleDropdownRef.current && !userRoleDropdownRef.current.contains(event.target as Node)) {
                setIsUserRoleDropdownOpen(false);
            }
            if (specificUserDropdownRef.current && !specificUserDropdownRef.current.contains(event.target as Node)) {
                setIsSpecificUserDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);


    const filteredUsers = useMemo(() => {
        const nonAdminUsers = users.filter(u => u.role !== 'admin'); // Exclude admin users
        if (formData.userRole === 'all') return nonAdminUsers;
        return nonAdminUsers.filter(u => u.role === formData.userRole);
    }, [users, formData.userRole]);

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.title.trim()) {
            newErrors.title = 'SOP Title is required';
        }

        if (!formData.description.trim()) {
            newErrors.description = 'SOP Description is required';
        }

        if (!formData.userRole) {
            newErrors.userRole = 'User Role is required';
        }

        if (formData.specificUsers.length === 0) {
            newErrors.specificUsers = 'At least one specific user is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'title') {
            if (!value.trim()) {
                newErrors.title = 'SOP Title is required';
            } else {
                delete newErrors.title;
            }
        } else if (fieldName === 'description') {
            if (!value.trim()) {
                newErrors.description = 'SOP Description is required';
            } else {
                delete newErrors.description;
            }
        } else if (fieldName === 'userRole') {
            if (!value) {
                newErrors.userRole = 'User Role is required';
            } else {
                delete newErrors.userRole;
                // Clear specific users error when role is selected and users are auto-selected
                if (formData.specificUsers.length > 0) {
                    delete newErrors.specificUsers;
                }
            }
        } else if (fieldName === 'specificUsers') {
            if (formData.specificUsers.length === 0) {
                newErrors.specificUsers = 'At least one specific user is required';
            } else {
                delete newErrors.specificUsers;
            }
        }

        setErrors(newErrors);
    };

    // Function to determine user role based on selected users
    const determineUserRoleFromUsers = (selectedUsers: string[]) => {
        if (selectedUsers.length === 0) return formData.userRole;

        // Get all users and their roles, excluding admin users
        const userRoles = selectedUsers.map(userEmail => {
            const user = users.find(u => u.email === userEmail);
            return user?.role || 'worker'; // Default to worker if role not found
        }).filter(role => role !== 'admin'); // Exclude admin users


        if (userRoles.length === 0) return formData.userRole; // If only admin users selected, keep current role

        // Check if all selected users are managers
        const allManagers = userRoles.every(role => role === 'manager');
        // Check if all selected users are workers
        const allWorkers = userRoles.every(role => role === 'worker');


        if (allManagers) return 'manager';
        if (allWorkers) return 'worker';

        // If mixed roles, keep the current selection or default to 'all'
        return formData.userRole === 'all' ? 'all' : formData.userRole;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            // Determine the appropriate user role based on selected users
            const finalUserRole = determineUserRoleFromUsers(formData.specificUsers);

            const updatedData = {
                ...sop,
                title: formData.title,
                description: formData.description,
                roles: [finalUserRole],
                users: formData.specificUsers.map(name => ({ name }))
            };

            toast.success("SOP updated successfully!");
            onUpdate(updatedData);
            onClose();
        } catch (error: any) {
            const errorMessage = error?.message || "Failed to update SOP. Please try again.";
            setErrors({ general: errorMessage });
            toast.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal show={true} onHide={onClose} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title>
                    Edit SOP
                </Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <form id="edit-sop-form" onSubmit={handleSubmit}>
                    {/* SOP Title */}
                    <div className="form-group">
                        <label htmlFor="editSopTitle">SOP Title</label>
                        <input
                            id="editSopTitle"
                            type="text"
                            value={formData.title}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, title: e.target.value }));
                                validateField('title', e.target.value);
                            }}
                            className={`form-control ${errors.title ? 'border-danger' : ''}`}
                            placeholder="Enter SOP title"
                        />
                        {errors.title && (
                            <p className="error-message">{errors.title}</p>
                        )}
                    </div>

                    {/* SOP Description */}
                    <div className="form-group">
                        <label htmlFor="editSopDescription">SOP Description</label>
                        <textarea
                            id="editSopDescription"
                            value={formData.description}
                            onChange={(e) => {
                                setFormData(prev => ({ ...prev, description: e.target.value }));
                                validateField('description', e.target.value);
                            }}
                            rows={3}
                            className={`form-control ${errors.description ? 'border-danger' : ''}`}
                            placeholder="Enter SOP description"
                        />
                        {errors.description && (
                            <p className="error-message">{errors.description}</p>
                        )}
                    </div>

                    {/* User Role and Specific Users Row */}
                    <Row>
                        <Col md={6}>
                            <div className="form-group">
                                <label htmlFor="editUserRole">User Role</label>
                                <div className="relative" ref={userRoleDropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => setIsUserRoleDropdownOpen(!isUserRoleDropdownOpen)}
                                        className={`custom-dropdown-btn ${errors.userRole ? 'border-danger' : ''}`}
                                    >
                                        <span className={formData.userRole === '' ? 'grey-500' : 'grey-800'}>
                                            {formData.userRole === '' ? 'Select User Role' : formData.userRole === 'all' ? 'All' : formData.userRole === 'manager' ? 'Manager' : 'Field Worker'}
                                        </span>
                                        <ChevronDown className={`w-4 h-4 grey-500 transition-transform ${isUserRoleDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isUserRoleDropdownOpen && (
                                        <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-lg" style={{ zIndex: 10 }}>
                                            <div className="py-1">
                                                <button
                                                    type="button"
                                                    onClick={() => { setFormData(prev => ({ ...prev, userRole: '' })); setIsUserRoleDropdownOpen(false); validateField('userRole', ''); }}
                                                    className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                >
                                                    Select User Role
                                                </button>
                                                <div className="border-top border-dashed"></div>
                                                <button
                                                    type="button"
                                                    onClick={() => { setFormData(prev => ({ ...prev, userRole: 'all' })); setIsUserRoleDropdownOpen(false); validateField('userRole', 'all'); }}
                                                    className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                >
                                                    All
                                                </button>
                                                <div className="border-top border-dashed"></div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, userRole: 'manager' }));
                                                        setIsUserRoleDropdownOpen(false);
                                                        validateField('userRole', 'manager');
                                                    }}
                                                    className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                >
                                                    Manager
                                                </button>
                                                <div className="border-top border-dashed"></div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setFormData(prev => ({ ...prev, userRole: 'worker' }));
                                                        setIsUserRoleDropdownOpen(false);
                                                        validateField('userRole', 'worker');
                                                    }}
                                                    className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                >
                                                    Field Worker
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {errors.userRole && (
                                    <p className="error-message">{errors.userRole}</p>
                                )}
                            </div>
                        </Col>

                        <Col md={6}>
                            <div className="form-group">
                                <label htmlFor="specificUsers">Specific Users</label>
                                <div className="relative" ref={specificUserDropdownRef}>
                                    <button
                                        type="button"
                                        onClick={() => setIsSpecificUserDropdownOpen(!isSpecificUserDropdownOpen)}
                                        className={`custom-dropdown-btn ${errors.specificUsers ? 'border-danger' : ''}`}
                                    >
                                        <span className={formData.specificUsers.length === 0 ? 'text-gray-500' : 'text-gray-700'}>
                                            {formData.specificUsers.length === 0 ? 'Select Specific Users' : `${formData.specificUsers.length} user(s) selected`}
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isSpecificUserDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isSpecificUserDropdownOpen && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto custom-dropdown">
                                            <div className="py-1">
                                                {usersLoading ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                        Loading users...
                                                    </div>
                                                ) : filteredUsers.length === 0 ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                        No users available
                                                    </div>
                                                ) : (
                                                    filteredUsers
                                                        .filter(user => {
                                                            if (user.role === 'admin') return false; // Exclude admin users
                                                            if (formData.userRole === 'all') return true;
                                                            if (formData.userRole === 'manager') return user.role === 'manager';
                                                            if (formData.userRole === 'worker') return user.role === 'worker';
                                                            return true;
                                                        })
                                                        .map((user) => {
                                                            const userIdentifier = user.email;
                                                            const isSelected = formData.specificUsers.includes(userIdentifier) ||
                                                                formData.specificUsers.includes(user.name) ||
                                                                formData.specificUsers.includes(user.id);
                                                            return (
                                                                <button
                                                                    key={user.id}
                                                                    type="button"
                                                                    onClick={() => {
                                                                        // Prevent deselecting if this is the last selected user
                                                                        if (isSelected && formData.specificUsers.length === 1) {
                                                                            return; // Don't allow deselecting the last user
                                                                        }

                                                                        let newSelectedUsers: string[];
                                                                        if (isSelected) {
                                                                            // Remove user
                                                                            newSelectedUsers = formData.specificUsers.filter(u => u !== userIdentifier);
                                                                        } else {
                                                                            // Add user
                                                                            newSelectedUsers = formData.specificUsers.includes(userIdentifier)
                                                                                ? formData.specificUsers
                                                                                : [...formData.specificUsers, userIdentifier];
                                                                        }

                                                                        // Use the new handler with auto-detection
                                                                        handleUserSelectionChange(newSelectedUsers);

                                                                        // Clear error when user selection changes
                                                                        setErrors(prev => {
                                                                            const newErrors = { ...prev };
                                                                            delete newErrors.specificUsers;
                                                                            return newErrors;
                                                                        });
                                                                    }}
                                                                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${isSelected && formData.specificUsers.length === 1
                                                                        ? 'text-gray-400 cursor-not-allowed'
                                                                        : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                    disabled={isSelected && formData.specificUsers.length === 1}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => { }}
                                                                        disabled={isSelected && formData.specificUsers.length === 1}
                                                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                                                    />
                                                                    {user.name} - {user.role}
                                                                    {isSelected && formData.specificUsers.length === 1 && (
                                                                        <span className="text-xs text-gray-400 ml-auto">(Required)</span>
                                                                    )}
                                                                </button>
                                                            );
                                                        })
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {errors.specificUsers && (
                                    <p className="error-message">{errors.specificUsers}</p>
                                )}
                            </div>
                        </Col>
                    </Row>


                    {/* PDF File */}
                    <div className="form-group">
                        {/* <label className="text-16-bold grey-800">PDF File:</label> */}
                        <div className="border-2 border-dashed border-secondary rounded p-4">
                            <div className="d-flex align-items-center gap-3">
                                <img src="/icons/pdfIcon.png" alt="PDF" className="w-8 h-8" />
                                <div>
                                    <div className="text-16-bold grey-800">
                                        {sop.file_name
                                            ? sop.file_name
                                            : sop.title
                                        }
                                    </div>
                                    <div className="text-14-reg status-green">Uploaded successfully</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* General Error Message */}
                    {errors.general && (
                        <div className="alert alert-danger" role="alert">
                            {errors.general}
                        </div>
                    )}
                </form>
                <div className="d-flex justify-content-end gap-3 mt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="renegade-btn-secondary mini-btn"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="edit-sop-form"
                        disabled={isLoading}
                        className="renegade-btn-primary mini-btn"
                        onClick={handleSubmit}
                    >
                        {isLoading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Updating SOP...
                            </>
                        ) : (
                            'Update SOP'
                        )}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

function AddNewSopModal({
    onClose,
    onSuccess,
}: {
    onClose: () => void;
    onSuccess: (sopTitle: string) => void;
}) {
    const { submitSopUpload } = useUpload();
    const [sopTitle, setSopTitle] = useState("");
    const [sopDescription, setSopDescription] = useState("");
    const [userRole, setUserRole] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUserRoleDropdownOpen, setIsUserRoleDropdownOpen] = useState(false);
    const [isSpecificUserDropdownOpen, setIsSpecificUserDropdownOpen] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const userRoleDropdownRef = useRef<HTMLDivElement>(null);
    const specificUserDropdownRef = useRef<HTMLDivElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Validation errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Reset all form data and errors when modal closes
    const resetForm = () => {
        setSopTitle("");
        setSopDescription("");
        setUserRole("");
        setSelectedFile(null);
        setSelectedUsers([]);
        setErrors({});
        setIsUserRoleDropdownOpen(false);
        setIsSpecificUserDropdownOpen(false);
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!sopTitle.trim()) {
            newErrors.title = 'SOP Title is required';
        }

        if (!sopDescription.trim()) {
            newErrors.description = 'SOP Description is required';
        }

        if (!userRole) {
            newErrors.userRole = 'User Role is required';
        }

        if (!selectedFile) {
            newErrors.file = 'PDF file is required';
        }

        if (selectedUsers.length === 0) {
            newErrors.specificUsers = 'At least one specific user is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'title') {
            if (!value.trim()) {
                newErrors.title = 'SOP Title is required';
            } else {
                delete newErrors.title;
            }
        } else if (fieldName === 'description') {
            if (!value.trim()) {
                newErrors.description = 'SOP Description is required';
            } else {
                delete newErrors.description;
            }
        } else if (fieldName === 'userRole') {
            if (!value) {
                newErrors.userRole = 'User Role is required';
            } else {
                delete newErrors.userRole;
                // Clear specific users error when role is selected and users are auto-selected
                if (selectedUsers.length > 0) {
                    delete newErrors.specificUsers;
                }
            }
        } else if (fieldName === 'file') {
            if (!value) {
                newErrors.file = 'PDF file is required';
            } else {
                delete newErrors.file;
            }
        } else if (fieldName === 'specificUsers') {
            if (selectedUsers.length === 0) {
                newErrors.specificUsers = 'At least one specific user is required';
            } else {
                delete newErrors.specificUsers;
            }
        }

        setErrors(newErrors);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Check file type (PDF only)
        if (file.type !== "application/pdf") {
            setErrors({ ...errors, file: 'Only PDF files are allowed' });
            event.target.value = '';
            return;
        }

        // Check file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
            setErrors({ ...errors, file: 'File size must be less than 10MB' });
            event.target.value = '';
            return;
        }

        // File is valid
        setSelectedFile(file);
        validateField('file', file.name);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        if (!validateForm()) {
            setIsSubmitting(false);
            return;
        }

        const roleMap: Record<string, string> = {
            worker: "worker",
            all: "all",
            manager: "manager",
            field_worker: "worker",
        };
        const normalizedRole = roleMap[userRole] || "worker";
        const file = selectedFile as File;
        const title = sopTitle.trim();

        try {
            await submitSopUpload({
                file,
                sopTitle: title,
                onClose,
                createMetadata: async (fileId) => {
                    logger.log("[SOP Upload] Add New SOP: calling createSop", {
                        fileId,
                        title,
                    });
                    await createSop({
                        file_id: fileId,
                        title,
                        description: sopDescription.trim(),
                        user_role: normalizedRole,
                        specific_users: selectedUsers,
                        file_name: file.name || "document.pdf",
                    });
                },
                getAdminEmail: async () => {
                    const userProfile = await fetchUserProfile();
                    return userProfile.email;
                },
                onMetadataCreated: () => {
                    toast.success(
                        `SOP "${truncateText(title, 20)}" upload started - processing in background`,
                    );
                    resetForm();
                    onSuccess(title);
                },
            });
        } catch (error: unknown) {
            const err = error as { message?: string };
            logger.error("[SOP Upload] Add New SOP error:", err);
            toast.error(err?.message || "Failed to upload SOP. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Fetch users when modal opens
    useEffect(() => {
        const loadUsers = async () => {
            try {
                setUsersLoading(true);
                const res = await fetchUsers({ page: 1, pageSize: 100, status: 'active' });
                setUsers(res.items);
            } catch (error) {
                logger.error('Error fetching users:', error);
            } finally {
                setUsersLoading(false);
            }
        };
        loadUsers();
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (userRoleDropdownRef.current && !userRoleDropdownRef.current.contains(event.target as Node)) {
                setIsUserRoleDropdownOpen(false);
            }
            if (specificUserDropdownRef.current && !specificUserDropdownRef.current.contains(event.target as Node)) {
                setIsSpecificUserDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleUserSelection = (userEmail: string) => {
        setSelectedUsers(prev => {
            const newSelection = prev.includes(userEmail)
                ? prev.filter(email => email !== userEmail)
                : [...prev, userEmail];

            // Ensure at least one user is always selected
            if (newSelection.length === 0) {
                return prev; // Don't allow deselecting the last user
            }

            return newSelection;
        });
    };

    // Auto-select users when role changes
    const handleRoleChange = (newRole: string) => {
        setUserRole(newRole);
        validateField('userRole', newRole);

        // Auto-select all users based on the selected role (excluding admin users)
        const filteredUsers = users.filter(user => {
            if (user.role === 'admin') return false; // Exclude admin users
            if (newRole === 'all') return true;
            if (newRole === 'manager') return user.role === 'manager';
            if (newRole === 'worker') return user.role === 'worker';
            return true;
        });

        const userEmails = filteredUsers.map(user => user.email);
        setSelectedUsers(userEmails);

        // Clear specific users error if users are auto-selected
        if (userEmails.length > 0) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.specificUsers;
                return newErrors;
            });
        }
    };

    return (
        <Modal show={true} onHide={() => { if (!isSubmitting) { resetForm(); onClose(); } }} className="std-modal-width add-sop-modal" centered backdrop="static">
            <Modal.Header>
                <Modal.Title>
                    Add New SOP
                </Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={() => { resetForm(); onClose(); }} aria-label="Close (escape if upload is stuck)" title="Close" />
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <form id="sop-form" onSubmit={handleSubmit}>
                    {/* SOP Title */}
                    <div className="form-group">
                        <label htmlFor="sopTitle">SOP Title</label>
                        <input
                            id="sopTitle"
                            type="text"
                            value={sopTitle}
                            onChange={(e) => {
                                setSopTitle(e.target.value);
                                validateField('title', e.target.value);
                            }}
                            placeholder="Enter SOP Title"
                            className={`form-control ${errors.title ? 'border-danger' : ''}`}
                            disabled={isSubmitting}
                            readOnly={isSubmitting}
                        />
                        {errors.title && (
                            <p className="error-message">{errors.title}</p>
                        )}
                    </div>

                    {/* SOP Description */}
                    <div className="form-group">
                        <label htmlFor="sopDescription">SOP Description</label>
                        <textarea
                            id="sopDescription"
                            value={sopDescription}
                            onChange={(e) => {
                                setSopDescription(e.target.value);
                                validateField('description', e.target.value);
                            }}
                            placeholder="Enter SOP Description"
                            rows={4}
                            className={`form-control ${errors.description ? 'border-danger' : ''}`}
                            disabled={isSubmitting}
                            readOnly={isSubmitting}
                        />
                        {errors.description && (
                            <p className="error-message">{errors.description}</p>
                        )}
                    </div>

                    {/* User Role & Specific Users */}
                    <Row>
                        <Col md={6}>
                            <div className="form-group">
                                <label htmlFor="userRole">User Role</label>
                                <div className="relative" ref={userRoleDropdownRef}>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => { if (!isSubmitting) setIsUserRoleDropdownOpen(!isUserRoleDropdownOpen); }}
                                        className={`custom-dropdown-btn ${errors.userRole ? 'border-danger' : ''} ${isSubmitting ? 'cursor-not-allowed opacity-70' : ''}`}
                                    >
                                        <span className={userRole === '' ? 'grey-500' : 'grey-800'}>
                                            {userRole === '' ? 'Select User Role' : userRole === 'all' ? 'All' : userRole === 'manager' ? 'Manager' : 'Field Worker'}
                                        </span>
                                        <ChevronDown className={`w-4 h-4 grey-500 transition-transform ${isUserRoleDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isUserRoleDropdownOpen && (
                                        <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-lg" style={{ zIndex: 10 }}>
                                            <div className="py-1">
                                                <button
                                                    type="button"
                                                    onClick={() => { setUserRole(''); setSelectedUsers([]); setIsUserRoleDropdownOpen(false); validateField('userRole', ''); }}
                                                    className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                >
                                                    Select User Role
                                                </button>
                                                <div className="border-top border-dashed"></div>
                                                <button
                                                    type="button"
                                                    onClick={() => { handleRoleChange('all'); setIsUserRoleDropdownOpen(false); }}
                                                    className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                >
                                                    All
                                                </button>
                                                <div className="border-top border-dashed"></div>
                                                <button
                                                    type="button"
                                                    onClick={() => { handleRoleChange('manager'); setIsUserRoleDropdownOpen(false); }}
                                                    className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                >
                                                    Manager
                                                </button>
                                                <div className="border-top border-dashed"></div>
                                                <button
                                                    type="button"
                                                    onClick={() => { handleRoleChange('worker'); setIsUserRoleDropdownOpen(false); }}
                                                    className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                >
                                                    Field Worker
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {errors.userRole && (
                                    <p className="error-message">{errors.userRole}</p>
                                )}
                            </div>
                        </Col>
                        <Col md={6}>
                            <div className="form-group">
                                <label htmlFor="specificUsers">Specific Users</label>
                                <div className="relative" ref={specificUserDropdownRef}>
                                    <button
                                        type="button"
                                        disabled={isSubmitting}
                                        onClick={() => { if (!isSubmitting) setIsSpecificUserDropdownOpen(!isSpecificUserDropdownOpen); }}
                                        className={`custom-dropdown-btn ${errors.specificUsers ? 'border-danger' : ''} ${isSubmitting ? 'cursor-not-allowed opacity-70' : ''}`}
                                    >
                                        <span className={selectedUsers.length === 0 ? 'text-gray-500' : 'text-gray-700'}>
                                            {selectedUsers.length === 0 ? 'Select Specific Users' : `${selectedUsers.length} user(s) selected`}
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isSpecificUserDropdownOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {isSpecificUserDropdownOpen && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto custom-dropdown">
                                            <div className="py-1">
                                                {usersLoading ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                        Loading users...
                                                    </div>
                                                ) : users.length === 0 ? (
                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                        No users available
                                                    </div>
                                                ) : (
                                                    users
                                                        .filter(user => {
                                                            if (user.role === 'admin') return false; // Exclude admin users
                                                            if (userRole === 'all') return true;
                                                            if (userRole === 'manager') return user.role === 'manager';
                                                            if (userRole === 'worker') return user.role === 'worker';
                                                            return true;
                                                        })
                                                        .map((user) => (
                                                            <button
                                                                key={user.email}
                                                                type="button"
                                                                onClick={() => {
                                                                    // Prevent deselecting if this is the last selected user
                                                                    if (selectedUsers.includes(user.email) && selectedUsers.length === 1) {
                                                                        return; // Don't allow deselecting the last user
                                                                    }
                                                                    toggleUserSelection(user.email);
                                                                    // Clear error when user selection changes
                                                                    setErrors(prev => {
                                                                        const newErrors = { ...prev };
                                                                        delete newErrors.specificUsers;
                                                                        return newErrors;
                                                                    });
                                                                }}
                                                                className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${selectedUsers.includes(user.email) && selectedUsers.length === 1
                                                                    ? 'text-gray-400 cursor-not-allowed'
                                                                    : 'text-gray-700 hover:bg-gray-100'
                                                                    }`}
                                                                disabled={selectedUsers.includes(user.email) && selectedUsers.length === 1}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedUsers.includes(user.email)}
                                                                    onChange={() => { }}
                                                                    disabled={selectedUsers.includes(user.email) && selectedUsers.length === 1}
                                                                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                                                />
                                                                {user.name} - {user.role}
                                                                {selectedUsers.includes(user.email) && selectedUsers.length === 1 && (
                                                                    <span className="text-xs text-gray-400 ml-auto">(Required)</span>
                                                                )}
                                                            </button>
                                                        ))
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {errors.specificUsers && (
                                    <p className="error-message">{errors.specificUsers}</p>
                                )}
                            </div>
                        </Col>
                    </Row>

                    {/* PDF File Upload */}
                    <div className="form-group">
                        <label htmlFor="file-upload">PDF File</label>
                        <div className={`border-2 border-dashed ${errors.file ? 'border-red-500' : 'border-gray-300'} rounded-md p-8 text-center transition-colors ${isSubmitting ? 'pointer-events-none opacity-70' : 'hover:border-red-400'}`}>
                            <input
                                type="file"
                                accept=".pdf"
                                onChange={handleFileChange}
                                className="d-none"
                                id="file-upload"
                                disabled={isSubmitting}
                            />
                            <label htmlFor="file-upload" className={isSubmitting ? 'cursor-not-allowed' : 'cursor-pointer'}>
                                <div className="d-flex flex-column align-items-center">
                                    <Upload size={32} className="grey-500 mb-2" />
                                    <p className="text-16-bold grey-800">Click to upload PDF</p>
                                    <p className="text-14-reg grey-500">PDF files only</p>
                                    <p className="text-12-reg grey-400 mt-1">(Max File Size 10mb)</p>
                                </div>
                            </label>
                            {selectedFile && (
                                <p className="mt-2 text-14-reg status-green">{selectedFile.name}</p>
                            )}
                        </div>
                        {errors.file && (
                            <p className="error-message">{errors.file}</p>
                        )}
                    </div>

                    {/* General Error Message */}
                    {errors.general && (
                        <div className="alert alert-danger" role="alert">
                            {errors.general}
                        </div>
                    )}
                </form>

                <div className="d-flex justify-content-end gap-3 mt-4">
                    <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => { if (!isSubmitting) { resetForm(); onClose(); } }}
                        className={`renegade-btn-secondary mini-btn ${isSubmitting ? 'cursor-not-allowed' : ''}`}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="sop-form"
                        className="renegade-btn-primary mini-btn"
                        disabled={isSubmitting}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Uploading SOP...
                            </>
                        ) : (
                            ' Upload SOP'
                        )}
                    </button>
                </div>
            </Modal.Body>

        </Modal>
    );
}

export default AdminManageSop;
